import { create } from 'zustand';

export interface GlobalUpload {
  id: string;
  fileName: string;
  progress: number;
  status: 'uploading' | 'processing' | 'success' | 'error';
}

export interface GlobalGeneration {
  id: string;
  type: 'flashcards' | 'quiz' | 'practice-test' | 'lecture' | 'podcast';
  label: string;
  status: 'generating' | 'ready' | 'error';
  startedAt: number;
  timeConstant: number;
}

interface UIState {
  sidebarCollapsed: boolean;
  mobileMenuOpen: boolean;
  activeModal: string | null;
  searchOpen: boolean;
  studySessionActive: boolean;
  pageHasContent: boolean;
  globalUploads: GlobalUpload[];
  globalGenerations: GlobalGeneration[];
  toggleSidebar: () => void;
  setMobileMenuOpen: (open: boolean) => void;
  openModal: (modalId: string) => void;
  closeModal: () => void;
  openSearch: () => void;
  closeSearch: () => void;
  setStudySessionActive: (active: boolean) => void;
  setPageHasContent: (hasContent: boolean) => void;
  addGlobalUpload: (upload: GlobalUpload) => void;
  updateGlobalUpload: (id: string, updates: Partial<GlobalUpload>) => void;
  removeGlobalUpload: (id: string) => void;
  addGlobalGeneration: (gen: GlobalGeneration) => void;
  updateGlobalGeneration: (id: string, updates: Partial<GlobalGeneration>) => void;
  removeGlobalGeneration: (id: string) => void;
}

export const useUIStore = create<UIState>((set) => ({
  sidebarCollapsed: false,
  mobileMenuOpen: false,
  activeModal: null,
  searchOpen: false,
  studySessionActive: false,
  pageHasContent: false,
  globalUploads: [],
  globalGenerations: [],
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),
  openModal: (modalId) => set({ activeModal: modalId }),
  closeModal: () => set({ activeModal: null }),
  openSearch: () => set({ searchOpen: true }),
  closeSearch: () => set({ searchOpen: false }),
  setStudySessionActive: (studySessionActive) => set({ studySessionActive }),
  setPageHasContent: (pageHasContent) => set({ pageHasContent }),
  addGlobalUpload: (upload) =>
    set((state) => ({ globalUploads: [...state.globalUploads, upload] })),
  updateGlobalUpload: (id, updates) =>
    set((state) => ({
      globalUploads: state.globalUploads.map((u) =>
        u.id === id ? { ...u, ...updates } : u
      ),
    })),
  removeGlobalUpload: (id) =>
    set((state) => ({
      globalUploads: state.globalUploads.filter((u) => u.id !== id),
    })),
  addGlobalGeneration: (gen) =>
    set((state) => ({
      globalGenerations: state.globalGenerations.some((g) => g.id === gen.id)
        ? state.globalGenerations
        : [...state.globalGenerations, gen],
    })),
  updateGlobalGeneration: (id, updates) =>
    set((state) => ({
      globalGenerations: state.globalGenerations.map((g) =>
        g.id === id ? { ...g, ...updates } : g
      ),
    })),
  removeGlobalGeneration: (id) =>
    set((state) => ({
      globalGenerations: state.globalGenerations.filter((g) => g.id !== id),
    })),
}));
